/*
 * Dashboard Page
 * 
 * Features:
 * - Show the news panel as the first dashboard section
 * - Display note/news cards with quick actions and chat integration
 * - Display the compact My Tasks panel with task metrics and filters
 * - Team-based filtering for notes display
 * 
 * Implementation:
 * - Recent News notes are fetched through a bounded server-side query
 * - Team filter state is retained locally and passed to the News query
 * 
 * Changes:
 * - 2026-05-13: Reworked the dashboard summary strip to match the new overdue/due/waiting/follow-up design.
 * - 2026-05-20: Replaced mock task summary cards with persisted task counts.
 * - 2026-06-02: Updated task summary wording to match the Todo/In Progress/Completed/Cancel workflow labels.
 * - 2026-06-15: Removed the duplicated top summary strip so task metrics live only in the My Tasks panel.
 * - 2026-07-21: Adopted the shared page layout for consistent shell-to-header and header-to-body spacing.
 * - 2026-09-02: Removed the redundant page label and tightened spacing between dashboard sections.
 * - 2026-09-02: Reduced the remaining inter-section whitespace to the compact 4px rhythm.
 * - 2026-09-02: Added 5px above My Tasks for a balanced 9px inter-section gap.
 * - 2026-09-02: Restored a visually hidden page heading for assistive navigation.
 * - 2026-09-08: Stopped downloading complete note history to render the 12-card News carousel.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { EVENTS } from '../constants/eventConstants';
import type { Note } from '../models/Note';
import type { Task } from '../models/Task';
import DashboardNotes from '../components/dashboard/DashboardNotes';
import DashboardTasks from '../components/dashboard/DashboardTasks';
import { useEventBus } from '../context/EventBusContext';
import { useTeams } from '../context/TeamContext';
import { getTasks, TASKS_UPDATED_EVENT } from '../services/taskService';
import PageLayout from '../components/patterns/PageLayout';



function Dashboard() {
  const { subscribe, unsubscribe } = useEventBus();
  const { currentUserId, currentAccessLevel, teams } = useTeams();
  const identityKey = JSON.stringify([currentUserId, currentAccessLevel, teams.map(team => [team.teamId, team.teamName]).sort()]);
  const newsLoad = useRef(0);
  const [newsNotes, setNewsNotes] = useState<Note[]>([]);
  const [userTasks, setUserTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<number | null>(null);

  // Get the selected team name for display
  const selectedTeamName = useMemo(() => {
    if (selectedTeamFilter === null) return null;
    if (selectedTeamFilter === 0) return "RLP";
    if (selectedTeamFilter === 9999) return "Public";

    const team = teams.find(t => t.teamId === selectedTeamFilter);
    return team ? team.teamName : `Team ${selectedTeamFilter}`;
  }, [selectedTeamFilter, teams]);

  // Handler to clear team filter
  const handleClearTeamFilter = () => {
    setSelectedTeamFilter(null);
  };

  const fetchNotes = useCallback(async () => {
    if (!currentUserId) return;

    const generation = ++newsLoad.current;
    try {
      setIsLoading(true);
      const notes = await request(API_ENDPOINTS.GET_USER_NOTES_PAGE, {
        limit: 12,
        search: null,
        teamId: selectedTeamFilter && selectedTeamFilter !== 9999 ? selectedTeamFilter : null,
        province: null,
        date: null,
        newsOnly: true,
        beforeUpdatedAt: null,
        beforeId: null,
        beforeAssociationType: null,
        beforeAssociationId: null,
      });

      if (generation === newsLoad.current) {
        setNewsNotes(Array.isArray(notes) ? notes : []);
      }
    } catch (error) {
      console.error('Failed to fetch user notes:', error);
    } finally {
      if (generation === newsLoad.current) setIsLoading(false);
    }
  }, [currentUserId, selectedTeamFilter, identityKey]);

  // Fetch all notes
  useEffect(() => {
    void fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    const handleNotesUpdated = () => {
      void fetchNotes();
    };

    subscribe(EVENTS.NOTES_UPDATED, handleNotesUpdated);

    return () => {
      unsubscribe(EVENTS.NOTES_UPDATED, handleNotesUpdated);
    };
  }, [fetchNotes, subscribe, unsubscribe]);

  useEffect(() => {
    if (!currentUserId) {
      setUserTasks([]);
      return;
    }

    let isMounted = true;

    const fetchTasks = async () => {
      setIsTasksLoading(true);
      try {
        const tasks = await getTasks({ assigneeId: currentUserId });
        if (isMounted) {
          setUserTasks(tasks);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to fetch user tasks:', error);
          setUserTasks([]);
        }
      } finally {
        if (isMounted) {
          setIsTasksLoading(false);
        }
      }
    };

    void fetchTasks();

    const handleTasksUpdated = () => {
      void fetchTasks();
    };

    window.addEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);
    };
  }, [currentUserId]);

  return (
    <PageLayout className="mx-6 overflow-hidden">
      <h1 className="sr-only">Dashboard</h1>

      {/* main content - fill available space */}
      <div className="flex-1 overflow-y-auto pb-6">
        <div className="space-y-[9px]">
          <DashboardNotes
            newsNotes={newsNotes}
            isLoading={isLoading}
            selectedTeamFilter={selectedTeamFilter}
            selectedTeamName={selectedTeamName}
            onClearTeamFilter={handleClearTeamFilter}
          />

          <DashboardTasks tasks={userTasks} isLoading={isTasksLoading} currentUserId={currentUserId} />
        </div>
      </div>

    </PageLayout>
  );
}

export default Dashboard;
